import {
  Alert,
  AlertType,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Grid,
  IconButton,
  IconColor,
  IconName,
  Input,
  InputVariant,
  Select,
  SelectVariant,
  Text,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { emptyArr } from '../../../../constants/misc'
import { useAudienceCountries } from '../../../../hooks/audience/useAudienceCountries'
import {
  CUSTOM_AUDIENCE_PRESET_NAME,
  useGetPresetAudiencesForLanguages
} from '../../../../hooks/audience/usePresetAudience'
import { AllocationRow } from '../../../../modules/Audience/LanguageAllocations/LanguageAllocations.control'
import { defaultAllocationsForCountry } from '../../../../modules/Audience/LanguageAllocations/LanguageAllocations.utils'
import {
  getCustomAudienceSelectItem,
  getPresetsSelectItems
} from '../../../../modules/Audience/utils'
import {
  CountryOption,
  dedupeCountriesToCountryOptions
} from '../../../../utils/HelperFunctions'
import { PositiveIntegerNumberFormatCustom } from '../../../../utils/NumberFormatCustom'
import { PanelSupplier } from '../../../../utils/audienceHelper'
import {
  AUDIENCE_LABEL_MAX_LENGTH,
  composeMarketName
} from '../../../../utils/marketFlags'

const DEFAULT_COMPLETES = 1000

export interface AddMarketDialogData {
  name: string
  countryCode: string
  // Primary locale language (first allocation) — sets the market's
  // fieldwork `locale.language`.
  countryLanguage: string
  // Per-language sample split (fractions, sum to 1.0). Seeded from the
  // country's curated default and fine-tuned later in the Market settings
  // drawer.
  languageAllocations: AllocationRow[]
  completes: number
  // Preset audience to apply to the market's fieldwork once it exists. Absent
  // for the default custom audience (nothing extra is sent).
  presetAudienceId?: string
}

// A committed row in the bulk table: one market to be created. The country is
// static (picked via the Autocomplete, changed by deleting + re-picking); the
// audience label and target completes are editable inline. `allocations` is
// seeded silently per country on add and refined post-add in the Market
// settings drawer.
interface MarketRow {
  // Stable, unique row identity. The same country may be added more than once
  // (e.g. a national market plus a regional boost), so the country code cannot
  // serve as the React key — use this instead.
  id: number
  countryCode: string
  // Country display name (e.g. "Belgium"), used both for the row label and to
  // build the market name on submit.
  name: string
  audienceLabel: string
  // empty string keeps the field clearable; the primary button stays disabled
  // until every row has a positive target.
  completes: number | ''
  allocations: AllocationRow[]
  // Selected preset audience; undefined keeps the default custom audience.
  // Preset ids are per-locale, so this id is only meaningful for the row's
  // own country + primary language.
  presetAudienceId?: string
}

// Per-row "Audience" dropdown. Preset audiences are defined per locale, so
// each row fetches the union of presets across all the languages it will
// field (e.g. a Belgium row offers both nl and fr presets). "Custom" is
// itself a preset returned by the backend and is the default: while it is
// selected no preset id is stored, and the market is created with the usual
// custom audience — mirroring the single-survey audience page.
const MarketAudienceSelect = ({
  countryName,
  countryCode,
  languages,
  presetAudienceId,
  disabled,
  onChange
}: {
  countryName: string
  countryCode: string
  // All the row's allocation languages, primary first.
  languages: string[]
  presetAudienceId?: string
  disabled: boolean
  onChange: (presetAudienceId?: string) => void
}) => {
  const { data: presets, loading } = useGetPresetAudiencesForLanguages(
    countryCode,
    languages
  )
  const options = useMemo(() => getPresetsSelectItems(presets), [presets])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // The cin-ui Select doesn't forward `disabled`, so guard here as well
    // (mirroring AudiencePresetPicker.control).
    if (disabled) return
    const preset = presets.find(
      (p) => p.presetAudienceId === event.target.value
    )
    if (!preset) return
    onChange(
      preset.name === CUSTOM_AUDIENCE_PRESET_NAME
        ? undefined
        : preset.presetAudienceId
    )
  }

  // While the presets load (or if the locale has none / the fetch failed),
  // hold the row's shape with a disabled select on the default — the market
  // is then simply created with a custom audience.
  if (loading || presets.length === 0) {
    return (
      <Select
        ariaLabel={`Audience for ${countryName}`}
        fullWidth
        variant={SelectVariant.Filled}
        optionValue=""
        options={[
          { id: 'custom-loading', name: CUSTOM_AUDIENCE_PRESET_NAME, value: '' }
        ]}
        // The cin-ui Select only honours readOnly (disabled isn't forwarded
        // to the underlying MUI Select).
        disabled
        readOnly
      />
    )
  }

  // The row holds no id while on the default, so display the locale's real
  // "Custom" preset in that case.
  const selectedValue =
    presetAudienceId ?? getCustomAudienceSelectItem(presets).value
  return (
    <Select
      ariaLabel={`Audience for ${countryName}`}
      fullWidth
      variant={SelectVariant.Filled}
      defaultOptionValue={selectedValue}
      optionValue={selectedValue}
      maxMenuHeight={300}
      options={options}
      onChange={handleChange}
      // Reclaim the trailing whitespace the Select reserves after the value —
      // the column is narrow and preset names like "GB Nat Rep (18+)" would
      // ellipsize otherwise.
      noWhiteSpaceAfterSelection
      // readOnly is what actually blocks interaction — the cin-ui Select
      // doesn't forward disabled (same workaround as AudiencePresetPicker).
      disabled={disabled}
      readOnly={disabled}
    />
  )
}

export const AddMarketDialog = ({
  open,
  loading,
  projectInProgress = false,
  onAdd,
  onClose
}: {
  open: boolean
  loading: boolean
  // True once any market has launched: the shared questionnaire is frozen, so
  // markets added now field it in full — surfaced as a warning below.
  projectInProgress?: boolean
  // Creates the markets sequentially and resolves with how many were created
  // before stopping — all of them on success, fewer on a partial failure. The
  // dialog drops exactly those rows so a retry only re-attempts the rest.
  onAdd: (rows: AddMarketDialogData[]) => void | Promise<number>
  onClose: () => void
}) => {
  const [rows, setRows] = useState<MarketRow[]>([])
  // Controlled Autocomplete text: kept while typing, cleared on select so the
  // picker resets to a blank search after each country is added.
  const [inputValue, setInputValue] = useState('')
  // Monotonic source of row ids. The same country can appear on several rows,
  // so rows need an identity independent of their country code.
  const nextRowId = useRef(0)

  // Markets are created on PureSpectrum; the supplier is switched per market
  // afterwards, in Market settings.
  const { data: dataCountries } = useAudienceCountries(
    PanelSupplier.PURESPECTRUM
  )

  // One row per country (Belgium's nl/fr locales collapse into a single
  // "Belgium"); the language split is seeded from the country's default.
  const options = useMemo(
    () => dedupeCountriesToCountryOptions(dataCountries ?? emptyArr),
    [dataCountries]
  )

  const handleSelectCountry = (option: CountryOption | null) => {
    if (!option) return
    // Compute the id in the handler (not the updater) so it is assigned exactly
    // once per selection.
    const id = nextRowId.current
    nextRowId.current += 1
    setRows((current) => [
      ...current,
      {
        id,
        countryCode: option.countryCode,
        name: option.name,
        audienceLabel: '',
        completes: DEFAULT_COMPLETES,
        // Seed the country's curated default split (e.g. Belgium nl 60% / fr
        // 40%), or the single primary language at 100%. Refined post-add in the
        // Market settings drawer.
        allocations: defaultAllocationsForCountry(
          dataCountries,
          option.countryCode
        )
      }
    ])
    setInputValue('')
  }

  const handleLabelChange = (index: number, value: string) => {
    setRows((current) =>
      current.map((row, i) =>
        i === index ? { ...row, audienceLabel: value } : row
      )
    )
  }

  const handleCompletesChange = (index: number, value: string) => {
    setRows((current) =>
      current.map((row, i) =>
        i === index ? { ...row, completes: value ? Number(value) : '' } : row
      )
    )
  }

  const handlePresetChange = (index: number, presetAudienceId?: string) => {
    setRows((current) =>
      current.map((row, i) =>
        i === index ? { ...row, presetAudienceId } : row
      )
    )
  }

  const handleRemoveRow = (index: number) => {
    setRows((current) => current.filter((_, i) => i !== index))
  }

  // Every row must have a positive target; at least one row is required.
  const savingDisabled =
    rows.length === 0 ||
    rows.some((row) => !row.completes || Number(row.completes) <= 0)

  // Reset only once the dialog is fully closed (open → false), never mid-submit.
  // A successful bulk add closes the dialog (parent flips `open`), which clears
  // it; a partial failure keeps the dialog open, so the researcher's rows
  // survive and can be retried. This also stops the rows flashing empty while
  // the sequential create loop is still in flight.
  useEffect(() => {
    if (!open) {
      setRows([])
      setInputValue('')
    }
  }, [open])

  const handleAdd = async () => {
    if (savingDisabled) {
      return
    }
    const submittedRows = rows
    const payload = submittedRows.map(
      (row): AddMarketDialogData => ({
        name: composeMarketName({
          countryPrefix: row.name,
          audienceLabel: row.audienceLabel,
          countryCode: row.countryCode
        }),
        countryCode: row.countryCode,
        // Primary locale language is the first allocation (a seeded split
        // always has at least one row).
        countryLanguage: row.allocations[0]?.language || 'en',
        languageAllocations: row.allocations,
        completes: Number(row.completes),
        presetAudienceId: row.presetAudienceId
      })
    )
    const createdCount = await onAdd(payload)
    // Drop the rows that were actually created, identified by their stable id
    // (never the country code — the same country may back several rows), so that
    // after a partial failure a retry only re-attempts the rows that remain. On
    // full success the parent closes the dialog, which clears the rows anyway.
    if (typeof createdCount === 'number' && createdCount > 0) {
      const createdIds = new Set(
        submittedRows.slice(0, createdCount).map((row) => row.id)
      )
      setRows((current) => current.filter((row) => !createdIds.has(row.id)))
    }
  }

  // Block dismissal while a bulk add is in flight — closing the dialog would
  // hide the spinner while markets keep being created in the background.
  const handleClose = () => {
    if (loading) {
      return
    }
    onClose()
  }

  return (
    <Dialog
      title="Add markets"
      open={open}
      fullWidth
      maxWidth={DialogMaxWidth.Md}
      variant={DialogVariant.DoubleButton}
      primaryButtonText="Add markets"
      // Also gated on `loading` so a second click can't kick off an
      // overlapping create run while the first is still in flight.
      primaryButtonDisabled={savingDisabled || loading}
      primaryButtonLoading={loading}
      primaryButtonClick={handleAdd}
      secondaryButtonText="Cancel"
      secondaryButtonClick={handleClose}
      transparentBackground
      onClickOutside={handleClose}
      onClose={handleClose}
    >
      <Grid container direction="column">
        {projectInProgress && (
          <Grid item sx={{ mb: 2 }}>
            <Alert severity={AlertType.WARNING}>
              Fieldwork has started, so the questionnaire is locked. Markets
              added now receive the full questionnaire — every question and
              answer option — and market visibility can no longer be adjusted
              for them. Markets adding a new language must be fully translated
              before they can launch.
            </Alert>
          </Grid>
        )}
        <Grid item sx={{ mb: 3 }}>
          <Text size={TextSize.m} weight={TextWeight.SemiBold}>
            Country
          </Text>
          <Autocomplete
            options={options}
            getOptionLabel={(option) => option.name}
            disabled={loading}
            // Always-null value keeps this a pure picker: selecting a country
            // appends a row and resets, rather than holding a "current"
            // selection.
            value={null}
            inputValue={inputValue}
            // Mirror MUI's input value verbatim: 'input' carries the typed
            // text; after a selection or on blur MUI emits '' (value is null),
            // which clears the box — so stale typed text never sticks.
            onInputChange={(_event, newInputValue) =>
              setInputValue(newInputValue)
            }
            onChange={(_event, option) => handleSelectCountry(option)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search countries…"
                inputProps={{ ...params.inputProps, 'aria-label': 'Country' }}
              />
            )}
          />
        </Grid>

        {rows.length > 0 && (
          <Grid item sx={{ maxHeight: 320, overflowY: 'auto', mb: 1, pr: 1 }}>
            {/* Column headers — the row inputs themselves are unlabeled
                (aria-labels only). Flex values and the trailing spacer (the
                delete IconButton's footprint) mirror the rows below. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                paddingBottom: 8
              }}
            >
              <div style={{ flex: 2, minWidth: 0 }}>
                <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                  Market
                </Text>
              </div>
              <div style={{ flex: 3, minWidth: 0 }}>
                <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                  Audience label
                </Text>
              </div>
              <div style={{ flex: 3, minWidth: 0 }}>
                <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                  Audience
                </Text>
              </div>
              <div style={{ flex: 2, minWidth: 0 }}>
                <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                  Target completes
                </Text>
              </div>
              <div style={{ width: 40 }} />
            </div>
            {rows.map((row, index) => (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  paddingBottom: 12
                }}
              >
                <div style={{ flex: 2, minWidth: 0 }}>
                  <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                    {row.name}
                  </Text>
                </div>
                <div style={{ flex: 3, minWidth: 0 }}>
                  <Input
                    ariaLabel={`Audience label for ${row.name}`}
                    fullWidth
                    variant={InputVariant.Filled}
                    placeholder="e.g. Scotland Boost"
                    value={row.audienceLabel}
                    maxLength={AUDIENCE_LABEL_MAX_LENGTH}
                    disabled={loading}
                    onChange={(event) =>
                      handleLabelChange(index, event.target.value)
                    }
                  />
                </div>
                <div style={{ flex: 3, minWidth: 0 }}>
                  <MarketAudienceSelect
                    countryName={row.name}
                    countryCode={row.countryCode}
                    // All the languages the market will field (the seeded
                    // split), primary first — presets of every locale are
                    // offered.
                    languages={
                      row.allocations.length > 0
                        ? row.allocations.map(
                            (allocation) => allocation.language
                          )
                        : ['en']
                    }
                    presetAudienceId={row.presetAudienceId}
                    disabled={loading}
                    onChange={(presetAudienceId) =>
                      handlePresetChange(index, presetAudienceId)
                    }
                  />
                </div>
                <div style={{ flex: 2, minWidth: 0 }}>
                  <Input
                    ariaLabel={`Target completes for ${row.name}`}
                    fullWidth
                    variant={InputVariant.Filled}
                    value={row.completes}
                    disabled={loading}
                    onChange={(event) =>
                      handleCompletesChange(index, event.target.value)
                    }
                    customFormat={PositiveIntegerNumberFormatCustom}
                  />
                </div>
                <IconButton
                  ariaLabel={`Remove ${row.name}`}
                  iconName={IconName.DeleteOutline}
                  iconColor={IconColor.Background}
                  disabled={loading}
                  onClick={() => handleRemoveRow(index)}
                />
              </div>
            ))}
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}
